import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { debounceAsync } from 'utils/misc/debounce';
import { fetchFilteredDepartmentData } from '../_api';
import { generateDepartmentDisplayValue } from '../_util';

const SchedulingDepartmentSearch = ({
	value,
	allValues,
	setValue = () => {},
	onBlur = () => {},
	onHelp = () => {},
	touched,
	error,
	helperText,
	customFilter = () => true,
	autoFocus = true,
	...props
}) => {
	const [sLoading, setLoading] = useState(false);
	const [sFocused, setFocused] = useState(false);

	const [options, setOptions] = useState([]);
	const dispatch = useDispatch();

	/**
	 * Memoized function required in order to preserve timeout IDs, otherwise function will be created new each render.
	 */
	const debouncedSearch = useMemo(() => {
		return debounceAsync(async input => {
			try {
				const processedInput = String(input).trim();
				const fetchResults = await fetchFilteredDepartmentData(processedInput);
				let optionsList = [];
				if (typeof fetchResults === 'object') {
					const options = Object.keys(fetchResults)
						.map(key => ({
							key: generateDepartmentDisplayValue(
								fetchResults[key],
							),
							value: fetchResults[key],
						}))
						.sort((a, b) => {
							if (
								a?.value?.departmentName <
								b?.value?.departmentName
							)
								return -1;
							if (
								a?.value?.departmentName >
								b?.value?.departmentName
							)
								return 1;
							return 0;
						});
					optionsList =
						options.length > 200 ? options.slice(0, 200) : options;
				}
				// exclude departments already selected in other rows, but not this row's own current value
				const existingOptionsList = allValues
					.filter(v => v.departmentId !== value?.departmentId)
					.map(v => v.departmentId);
				const processedOptionsList = optionsList.filter(
					o => !existingOptionsList.includes(o.value.departmentId),
				);
				const customFilteredProcessedOptionsList =
					processedOptionsList.filter(customFilter);
				setOptions(customFilteredProcessedOptionsList);
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			} finally {
				setLoading(false);
			}
		});
	}, [dispatch, allValues, customFilter, value?.departmentId]);
	return (
		<FormControl variant='filled' fullWidth>
			<Autocomplete
				autoComplete
				clearIcon={null}
				disableClearable
				filterOptions={options => options}
				forcePopupIcon={true}
				groupBy={option => option.category}
				getOptionLabel={option => (option.key ? option.key : option)}
				isOptionEqualToValue={(option, value) => {
					return option.value.departmentName === value;
				}}
				loading={sLoading}
				noOptionsText={'No results'}
				onChange={(_, newValue) => {
					if (newValue?.value) {
						setValue({
							...newValue?.value,
						});
					} else {
						setValue({});
					}
				}}
				onInputChange={(_, newInputValue) => {
					setLoading(true);
					debouncedSearch(newInputValue);
				}}
				options={options}
				renderInput={params => (
					<TextField
						{...params}
						autoFocus={autoFocus}
						fullWidth
						InputLabelProps={{ shrink: false }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									<InputAdornment
										sx={{
											marginBottom: '8px',
										}}
										position='end'
									>
										{sLoading ? (
											<CircularProgress
												size={20}
												variant='indeterminate'
											/>
										) : null}
									</InputAdornment>
									{params?.InputProps?.endAdornment}
								</>
							),
							placeholder: sFocused
								? 'Search for a department...'
								: '',
						}}
						onBlur={e => {
							if (value?.departmentName) {
								setOptions([
									{
										key: value.departmentName,
										value: value,
									},
								]);
							}
							onBlur(e);
							setFocused(false);
						}}
						name={props.name}
						size='small'
						variant='standard'
						{...props}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					const optionStr = option.key ? option.key : option;
					const matches = match(optionStr, inputValue, {
						insideWords: true,
						findAllOccurrences: true,
					});
					const parts = parse(optionStr, matches);

					return (
						<li {...props}>
							<div>
								{parts.map((part, index) => (
									<span
										key={index}
										style={{
											fontWeight: part.highlight
												? 700
												: 400,
										}}
									>
										{part.text}
									</span>
								))}
							</div>
						</li>
					);
				}}
				value={value.departmentName}
			/>

			<FormHelperText error={touched && error}>
				{touched && error}
			</FormHelperText>
		</FormControl>
	);
};
export default SchedulingDepartmentSearch;
